import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, API_BASE, cfmt } from '../lib/api';
import { useLang } from '../context/LanguageContext';
import SEOHead from '../components/common/SEOHead';
import { toast } from 'sonner';
import { FileDown, ArrowRight } from 'lucide-react';

export default function RetireeReport() {
    const { t } = useLang();
    const [origin, setOrigin] = useState('');
    const [value, setValue] = useState(1800000);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);

    const { data } = useQuery({
        queryKey: ['retiree-source-neighbourhoods'],
        queryFn: async () => (await api.get('/neighbourhoods', {
            params: { region: 'Halton Region', sort: 'price_desc' },
        })).data,
    });

    // Default origin to first Oakville pocket
    useEffect(() => {
        if (!origin && data?.items?.length) {
            const oakville = data.items.find((n) => n.city === 'Oakville') || data.items[0];
            setOrigin(oakville.slug);
            setValue(oakville.median_price);
        }
    }, [data, origin]);

    async function submit(e) {
        e.preventDefault();
        if (!origin || !email || busy) return;
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/reports/retiree-migration`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, origin_slug: origin, origin_home_value: Number(value), name }),
            });
            if (!res.ok) throw new Error('Report failed');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `retiree_migration_${origin}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Report downloaded. Your request has been received.');
        } catch (err) {
            toast.error('Report generation failed. Please try again.');
        } finally {
            setBusy(false);
        }
    }

    const selected = data?.items?.find((n) => n.slug === origin);

    return (
        <div>
            <SEOHead
                title="Retiree Migration Report — free PDF"
                description="Enter your Oakville, Burlington or Toronto home value and receive a bespoke PDF report on the five best Southern Ontario neighbourhoods worth downshifting to."
                path="/retiree-report"
                type="article"
            />
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-8">
                    <div className="overline">Free PDF report · Southern Ontario</div>
                    <h1 className="font-editorial text-[44px] lg:text-[68px] tracking-editorial mt-4 leading-[1.02]">
                        {t('retiree.title')}
                    </h1>
                    <p className="mt-6 font-editorial text-[20px] italic text-neutral-600 leading-relaxed max-w-3xl">
                        {t('retiree.dek')}
                    </p>
                </div>
            </section>

            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <form onSubmit={submit} className="lg:col-span-6 border border-neutral-200 p-8">
                        <label className="overline">{t('retiree.origin')}</label>
                        <select
                            data-testid="retiree-origin"
                            value={origin}
                            onChange={(e) => {
                                setOrigin(e.target.value);
                                const n = data?.items?.find((x) => x.slug === e.target.value);
                                if (n) setValue(n.median_price);
                            }}
                            className="mt-2 w-full border border-neutral-300 px-3 py-3 text-[15px] bg-white"
                           
                        >
                            {(data?.items || []).map((n) => (
                                <option key={n.slug} value={n.slug}>{n.name} · {n.city} (median {cfmt(n.median_price)})</option>
                            ))}
                        </select>

                        <label className="overline mt-6 block">{t('retiree.homeValue')}</label>
                        <input
                            data-testid="retiree-value"
                            type="number"
                            min={300000}
                            step={10000}
                            value={value}
                            onChange={(e) => setValue(Number(e.target.value))}
                            className="mt-2 w-full border border-neutral-300 px-3 py-3 text-[15px] font-mono-data tabular"
                           
                        />
                        <div className="mt-1 text-[12px] text-neutral-500">Prefilled to the neighbourhood median. Overwrite with your own estimate.</div>

                        <label className="overline mt-6 block">Your name (optional)</label>
                        <input
                            data-testid="retiree-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Jane Doe"
                            className="mt-2 w-full border border-neutral-300 px-3 py-3 text-[15px]"
                           
                        />

                        <label className="overline mt-6 block">{t('retiree.email')}</label>
                        <input
                            data-testid="retiree-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.ca"
                            className="mt-2 w-full border border-neutral-300 px-3 py-3 text-[15px]"
                           
                        />

                        <button
                            data-testid="retiree-submit"
                            type="submit"
                            disabled={busy}
                            className="mt-6 inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3.5 text-[14px] hover-invert hover:bg-brand-gold hover:text-brand-navy border border-brand-navy disabled:opacity-50"
                           
                        >
                            <FileDown className="w-4 h-4" />
                            {busy ? t('retiree.downloading') : t('retiree.submit')}
                            <ArrowRight className="w-4 h-4" />
                        </button>
                        <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-neutral-400 font-mono-data">
                            Free · No credit card · Unsubscribe anytime
                        </div>
                    </form>

                    <aside className="lg:col-span-6">
                        <div className="border-l-2 border-[#2F6B47] pl-6">
                            <div className="overline text-[#2F6B47]">Sample selection</div>
                            {selected && (
                                <>
                                    <div className="font-editorial text-[26px] tracking-editorial mt-3 leading-tight">
                                        Selling {selected.name}, {selected.city}
                                    </div>
                                    <div className="mt-3 text-[14px] text-neutral-600">
                                        With a home worth <span className="font-mono-data tabular text-black">{cfmt(value)}</span>, we screen for Niagara + Brant destinations
                                        that free at least $50,000 of capital, score above 80 on retiree suitability, and appreciate durably.
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="mt-10 border border-neutral-200 p-6 bg-[#F7F7F5]">
                            <div className="overline">What&apos;s inside the PDF</div>
                            <ul className="mt-4 space-y-2 text-[14px]">
                                <li>— Top 5 destinations ranked by combined retiree score</li>
                                <li>— One-page deep profile for each</li>
                                <li>— Capital freed per destination</li>
                                <li>— Hospitals & transit access per neighbourhood</li>
                                <li>— Trade-offs worth knowing</li>
                                <li>— Methodology (transparent scoring)</li>
                            </ul>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
